import { Link } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import styled from 'styled-components';
import { errorMessage } from '@/api/errors';
import { WBSButton } from '@/common/WBSButton';

interface ErrorScreenProps {
  /** The thrown value, if there was one. Its `message` is shown when the server sent one. */
  error?: unknown;
  /** Overrides the default title — use for a not-found screen. */
  title?: string;
  /** Retry handler; the button is hidden without one. */
  onRetry?: () => void;
}

/**
 * The screen behind the router's `errorComponent` and `notFoundComponent`.
 * Shows the server's `message` when there is one and a generic line otherwise;
 * `error_code` is for branching and is never shown.
 */
export function ErrorScreen({ error, title, onRetry }: ErrorScreenProps) {
  const { t } = useTranslation();

  const heading = title ?? t('wbs.something-went-wrong');
  const detail = errorMessage(error, t('wbs.something-went-wrong-please'));

  return (
    <Container role="alert">
      <Heading>{heading}</Heading>
      <Detail>{detail}</Detail>
      <Actions>
        {onRetry && (
          <WBSButton onClick={onRetry} type="button">
            {t('wbs.try-again')}
          </WBSButton>
        )}
        <Link to="/">{t('navigation.dashboard')}</Link>
      </Actions>
    </Container>
  );
}

const Container = styled.div`
  align-items: center;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  justify-content: center;
  min-height: 50vh;
  padding: 2rem 1rem;
  text-align: center;
`;

const Heading = styled.h1`
  color: var(--black);
  font-family: var(--raleway);
  font-size: 24px;
  font-weight: 600;
  margin: 0;
`;

const Detail = styled.p`
  color: var(--grey-600);
  font-family: var(--raleway);
  font-size: 16px;
  margin: 0;
  max-width: 40rem;
`;

const Actions = styled.div`
  align-items: center;
  display: flex;
  gap: 1rem;

  a {
    color: var(--wbs-red);
    font-family: var(--raleway);
    font-size: 16px;
    text-decoration: none;
  }
`;

/**
 * The router's `defaultErrorComponent`. TanStack passes the thrown value and a
 * `reset` that re-runs the route, which is the only retry that can work, since
 * the failure happened during the route's own load.
 */
export function DefaultErrorComponent({
  error,
  reset,
}: {
  error: unknown;
  reset: () => void;
}) {
  return <ErrorScreen error={error} onRetry={reset} />;
}

/** The router's `defaultNotFoundComponent`. */
export function DefaultNotFoundComponent() {
  return <NotFoundScreen />;
}

/** A 404, which is a different message from a failure even though it is the same layout. */
export function NotFoundScreen() {
  const { t } = useTranslation();
  return <ErrorScreen title={t('wbs.page-not-found')} />;
}
